import React, { useState, useEffect } from "react";
import { Typography, Grid, Container, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import ListGenericCard from "../../components/Custom/ListGenericCard";
import {
  CustomTitle,
} from "../../components/Custom/CustomTypography";
import RequestSubmitPopup from "../../components/Popup/RequestSubmitPopup";
import { useAlert } from "../../components/Alert";
import { useNavigate } from "react-router-dom";

export default function SendLoanProposalLender() {
  const { fpoId } = useParams();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fpoDetails, setFPODetails] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState("");
  const [loanProposalFormData, setLoanProposalFormData] = useState({});
  const [proposalId, setProposalId] = useState("");
  const [openLoanProposalPopup, setOpenLoanProposalPopup] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [documents, setDocuments] = useState([]);

  const fetchFPODetails = async () => {
    try {
      const response = await api.get(`/fpo/get-fpo-details?fpoId=${fpoId}`);
      setFPODetails(response.data);
      setDocuments(response.data.documents);
      setIsUnlocked(response.data.fpo_unlocked);
    } catch (error) {
      console.error("Error fetching FPO details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFPODetails();
  }, []);

  const handleTextFieldChange = (name, value) => {
    if (openPopup) {
      setTextFieldValue(value);
    } else if (openLoanProposalPopup) {
      setLoanProposalFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleOpenPopupLoanProposal = () => {
    setOpenLoanProposalPopup(true);
  };

  const handleDocumentRequestSubmit = async () => {
    if (proposalId) {
      const requestData = {
        document: textFieldValue,
        target_user: fpoId,
        loan_proposal: proposalId,
      };

      try {
        await api
          .post("lending/document-update-request/", requestData)
          .then((res) => {
            if (res && res.data) {
              navigate("/lender/prospect-borrowers");
              showAlert("success", "Document request sent successfully");
            }
          });
      } catch (error) {
        console.error("Error making POST request:", error);
        showAlert(
          "error",
          "An error occurred while sending the document request."
        );
      }
    }
  };

  const handleLoanProposalSubmit = async () => {
    const requestData = { ...loanProposalFormData, target_user: fpoId };

    try {
      const response = await api.post(
        "lending/create-loan-proposal/",
        requestData
      );
      if (response && response.data) {
        setProposalId(response.data.id); // Store the proposal ID
        showAlert("success", "Proposal sent successfully");
        setOpenLoanProposalPopup(false);
        setOpenPopup(true); // Open document request popup
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred while sending the loan proposal.";
      showAlert("error", errorMessage);
      setOpenLoanProposalPopup(false);
    }
  };

  const handleView = (documentUrl) => {
    window.open(documentUrl, "_blank"); // Open document in a new tab
  };

  const handleUnlockClick = async () => {
    const requestData = {
      fpo_id: fpoId,
    };

    try {
      await api
        .post("/lending/unlock_fpo/unlock_fpo_entry/", requestData)
        .then((res) => {
          if (res && res.data) {
            showAlert("success", "FPO documents unlocked successfully");
            setIsUnlocked(true);
            setDocuments(res.data.data.document);
          }
        });
    } catch (error) {
      console.log("Error while unlocking the FPO documents", error);
      showAlert("error", "Error while unlocking the documents");
    }
  };

  const infoItems = fpoDetails
    ? [
        {
          label: t("Name (Member)"),
          value: fpoDetails?.name || "NA",
        },
        // {
        //   label: t("Address"),
        //   value: fpoDetails.fpo_details_data?.address || "NA",
        // },
        { label: t("Contact"), value: fpoDetails.members?.[0]?.mobile ?? "NA" },
        {
          label: t("PAN Number"),
          value: fpoDetails.fpo_details_data?.pan_number ?? "NA",
        },
      ]
    : [];

  const documentRequestFields = [
    {
      type: "text",
      label: t("Document Type"),
      name: "document_type",
      required: true,
    },
  ];

  const loanProposalFields = [
    {
      type: "select",
      label: t("Loan Type"),
      name: "loan_type",
      options: [
        { value: "working capital", label: t("Working Capital") },
        { value: "invoice finance", label: t("Invoice Finance") },
        { value: "warehouse finance", label: t("Warehouse Finance") },
      ],
      required: true,
    },
    {
      type: "number",
      label: t("Amount"),
      name: "amount",
      required: true,
    },
    {
      type: "number",
      label: t("Enter Rate of Interest (%)"),
      name: "rate",
      required: true,
    },
  ];

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          sx={{
            height: "fit-content",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomTitle>
            {fpoDetails?.fpo_details_data?.name
              ? fpoDetails?.fpo_details_data?.name
              : ""}
          </CustomTitle>
          <Box mt={2}>
            {infoItems.map((item, index) => (
              <Grid container spacing={1} key={index}>
                <Grid item xs={6}>
                  <Typography variant="body1" fontWeight="bold">
                    {item.label}:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">{item.value}</Typography>
                </Grid>
              </Grid>
            ))}
          </Box>

          {documents?.[0] && (
            <Box mt={2}>
              <Typography variant="body1" fontWeight="bold">
                {t("Documents :")}
              </Typography>
              {documents?.map((document, index) => (
                <Box mt={1} key={index}>
                  <ListGenericCard
                    title={document.name}
                    onClick={() => handleView(document.document)}
                  />
                </Box>
              ))}
            </Box>
          )}

          {fpoDetails.fpo_details_data?.pan_card && (
            <Box mt={2}>
              <Typography variant="body1" fontWeight="bold">
                {t("PAN Card :")}
              </Typography>
              <img
                src={fpoDetails.fpo_details_data?.pan_card}
                alt="PAN Card"
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  marginTop: "10px",
                }}
              />
            </Box>
          )}

          {fpoDetails.farmers?.length > 0 && (
            <Box mt={1}>
              <ListGenericCard
                title={t("Farmer List")}
                // onClick={() => handleCardClick(fpoId)}
              />
            </Box>
          )}

          {!isUnlocked && (
            <CustomButton hideIcon onClick={handleUnlockClick}>
              {t("Unlock Documents")}
            </CustomButton>
          )}

          <CustomButton
            hideIcon
            onClick={handleOpenPopupLoanProposal}
            sx={{ mt: 2 }}
          >
            {t("Send Loan Proposal")}
          </CustomButton>
        </Container>
        <RequestSubmitPopup
          open={openPopup}
          handleClose={() => setOpenPopup(false)}
          title={t("Request Other Document")}
          textFieldLabel={t("Enter something")}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          handleSubmit={handleDocumentRequestSubmit}
          buttonTitle={t("Save")}
          formType="documentRequest"
          customFields={documentRequestFields}
          loanId={proposalId}
        />
        <RequestSubmitPopup
          open={openLoanProposalPopup}
          handleClose={() => setOpenLoanProposalPopup(false)}
          title={t("Loan Proposal")}
          textFieldLabel={t("Enter something")}
          textFieldValue={textFieldValue}
          handleTextFieldChange={handleTextFieldChange}
          handleSubmit={handleLoanProposalSubmit}
          buttonTitle={t("Send")}
          formType="loanProposal"
          customFields={loanProposalFields}
          fpoId={fpoId}
        />
      </LoaderContainer>
    </>
  );
}
